import { useCallback, useEffect, useRef } from "react";

const NEAR_BOTTOM_THRESHOLD = 100;

/**
 * Tracks whether the user has scrolled away from the bottom of a container.
 * Only auto-scrolls to the sentinel element when the user is near the bottom.
 */
export function useAutoScroll(deps: unknown[]) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const userScrolledUpRef = useRef(false);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const nearBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_THRESHOLD;
    userScrolledUpRef.current = !nearBottom;
  }, []);

  const resetScroll = useCallback(() => {
    userScrolledUpRef.current = false;
  }, []);

  useEffect(() => {
    if (!userScrolledUpRef.current) {
      sentinelRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return {
    scrollContainerRef,
    sentinelRef,
    handleScroll,
    resetScroll,
    /** Exposed for testing — true when user has scrolled away from bottom. */
    get isScrolledUp() {
      return userScrolledUpRef.current;
    },
  };
}
